<script setup lang="ts">
/**
 * The tank you are driving, at the top of the shop sidebar.
 *
 * **It is the real hull and the real turret now**, rendered from the authored
 * GLBs through the same cached still the catalogue cards and the garage floor's
 * bays use — `ItemPreview` with the `'tank'` framing, which is the one framing
 * that assembles both halves. It replaced a flat SVG plan drawing: two rounded
 * rectangles and a barrel whose length was a function of range, in the two hex
 * columns a `PAINT` row carries.
 *
 * That drawing was honest when it was written and had stopped being so. It was
 * the last consumer of `colorPrimary` / `colorSecondary` — the swatch the
 * Liveries tab was once pulled for selling — and it drew the *same* tank for all
 * 525 hull/turret pairs, on the one panel whose whole subject is which of them
 * you fitted. A picture that cannot tell a Drover from a Phantom is a diagram of
 * "a tank", not a preview of yours.
 *
 * It costs no WebGL context and no per-frame work: `model-thumbnail.ts` renders
 * each distinct pair once on a single offscreen engine and hands back a blob
 * URL, so this is an `<img>` like every card in the shop. That is also why the
 * old objection in this file — *spinning up a second Babylon scene to show
 * something that never moves* — no longer applies: there is no second scene, and
 * this asks for a still the garage has usually already drawn.
 *
 * The livery is deliberately **not** passed. `paintId` is read by the `'livery'`
 * framing only, so a tank still is the stock paint here exactly as it is in the
 * lobby's bays; painting it would mean a third cache key on a picture nobody is
 * buying from.
 */
import ItemPreview from '@/garage/ItemPreview.vue';

defineProps<{
  /** The equipped hull, or null while the garage is still answering. */
  hullId: string | null;
  /** The equipped turret, or null while the garage is still answering. */
  turretId: string | null;
  hullName: string;
  turretName: string;
}>();
</script>

<template>
  <figure class="tank">
    <ItemPreview
      :hull-id="hullId"
      :turret-id="turretId"
      framing="tank"
      :label="`${hullName} with ${turretName}`"
    />

    <figcaption>
      <strong>{{ hullName }}</strong>
      <span>{{ turretName }}</span>
    </figcaption>
  </figure>
</template>

<style scoped>
.tank {
  margin: 0;
  border: 1px solid var(--steel-700);
  border-radius: 4px;
  background: var(--steel-900);
  overflow: hidden;
}

/* The still brings its own frame for a card in a grid; here the figure is the
   frame, so the child's border and radius are taken off rather than nested. */
.tank :deep(.preview) {
  border: none;
  border-radius: 0;
}

figcaption {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  padding: 0.65rem 0.85rem;
  border-top: 1px solid var(--steel-700);
  font-size: 0.8rem;
}

figcaption strong {
  color: #fff;
  letter-spacing: 0.04em;
}

figcaption span {
  color: var(--steel-400);
}
</style>
